import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Grid, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import NotInterestedIcon from '@mui/icons-material/NotInterestedRounded';
import CustomDataGrid from '../../../reusable-components/datagrid/CustomDataGrid';
import { useDispatch, useSelector } from '../../../redux/store';
import { COMPLETED_INVOICES } from '../../../reusable-components/datagrid/invoiceColumns';
import { ConfirmDialog } from '../../../reusable-components/confirm-dialog';
import { useSnackbar } from '../../../reusable-components/snackbar';
import { getCompleteInvoices } from '../../../redux/slices/invoicing';
import { deleteInvoice } from '../../../api/invoicing';
import { HOST_API_URL } from '../../../config-global';
import PdfPopupButton from '../../../reusable-components/pdf-popup/PdfPopupButton';
import { fMonthDayYear } from '../../../utils/formatTime';
import { MarkedAsSentStatus } from '../../../utils/globalEnums';
import InvoiceStatusModalButton from './InvoiceStatusModalButton';

CompletedInvoices.propTypes = {
  CustomToolbar: PropTypes.func,
  setActiveRows: PropTypes.func,
};

export default function CompletedInvoices({ CustomToolbar, setActiveRows }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    data: { previousInvoicing },
  } = useSelector((state) => state.invoicing);
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const date = { startDate: searchParams.get('start'), endDate: searchParams.get('end') };
      await dispatch(getCompleteInvoices(date));
      setLoading(false);
    };
    fetchData();
  }, [searchParams, dispatch]);
  useEffect(() => {
    setData(previousInvoicing?.map(invoice => ({ ...invoice, status: invoice.alternateSent ? "Rejected" : invoice.bookingVendorStatus })));
  }, [previousInvoicing]);

  const handleDelete = (id) => {
    deleteInvoice(id)
      .then(() => {
        enqueueSnackbar('Invoice deleted successfully', { variant: 'success' });
        setData((data) => data.filter((invoice) => invoice.id !== id));
      })
      .catch(() => enqueueSnackbar('Something has gone wrong', { variant: 'error' }));
  };

  const StatusButtons = (params) => {
    const { alternateSent, invoiceBookedTo, markedAsSent, total, deleted } = params.row;

    return (
      <>
        <Tooltip
          title={
            deleted ? (
              <div>
                Deleted by {deleted.deletedBy} on: {deleted.dateDeleted && fMonthDayYear(deleted.dateDeleted)}
              </div>
            ) : alternateSent ? (
              <div>Booking vendor rejected invoice due to already having an invoice with that billing number</div>
            ) : !invoiceBookedTo ? (
              <>
                {markedAsSent ? (
                  <div>
                    <div>
                      {markedAsSent.status === MarkedAsSentStatus.ManuallyPushedBy
                        ? 'Invoice already exists in Intacct. Automatically marked as Processed.'
                        : markedAsSent.status === MarkedAsSentStatus.UpdateOrdersOnly
                          ? 'Account is Update Orders Only. Invoice was automatically marked as Processed.'
                          : markedAsSent.status === MarkedAsSentStatus.NoChargeMarkedSent
                            ? 'Total: $0.00. Automatically marked as Processed.'
                            : 'Invoice was automatically marked as Processed.'}
                    </div>
                    <div>Processed By: {markedAsSent.addedBy}</div>
                    <div>Processed on: {fMonthDayYear(markedAsSent?.dateAdded)}</div>
                  </div>
                ) : (
                  <div>Sent to booking vendor</div>
                )}
              </>
            ) : total === 0 ? (
              <div>
                <div>Total: $0.00. Automatically marked as Processed.</div>
              </div>
            ) : (
              <div>
                {invoiceBookedTo?.sentTo ? <div>Sent To: {invoiceBookedTo?.sentTo}</div> : ''}
                {invoiceBookedTo?.addedBy ? <div>Sent By: {invoiceBookedTo.addedBy}</div> : ''}
                {invoiceBookedTo?.dateAdded ? <div>Sent On: {fMonthDayYear(invoiceBookedTo?.dateAdded)}</div> : ''}
                {invoiceBookedTo?.sentAutomatically ? <div>Sent through the automatic invoice system</div> : ''}
              </div>
            )
          }
        >
          {deleted ? <CancelIcon color={'error'} />
            : total !== 0 ? <CheckCircleIcon color={alternateSent ? 'warning' : 'success'} />
              : total === 0 ? <NotInterestedIcon color={'warning'} />
                : ''}
        </Tooltip>
        <InvoiceStatusModalButton
          id={params.row.id}
          total={params.row.total}
          vendor={params.row.vendor}
          bookingVendorStatus={params.row.bookingVendorStatus}
          inline={false}
        />
      </>
    );
  };

  const status = {
    field: 'status',
    headerName: 'Status',
    width: 100,
    renderCell: (params) => StatusButtons(params),
    sortable: true,
    type: 'customSelect',
  };

  const actions = {
    field: 'actions',
    headerName: 'Actions',
    disableReorder: true,
    width: 100,
    id: 'actions',
    className: 'actions',
    sortable: false,
    renderCell: (params) => {
      const { id, editingLocked } = params.row;
      return (
        <>
          <Tooltip title={editingLocked ? 'Insufficient permissions for this facility' : ''} arrow>
            <span>
              <ConfirmDialog
                disabled={editingLocked}
                content={`Are you sure you want to delete this invoice?`}
                action={() => handleDelete(id)}
              />
            </span>
          </Tooltip>
          {params.row.hasMainFile && (
            <PdfPopupButton
              icon={<RemoveRedEyeIcon />}
              url={`${HOST_API_URL}Api/Invoices/ViewInvoiceFile?id=${params.row.id}&mainFile=true`}
              tooltipText={
                <iframe
                  style={{ width: '100%', height: '100%' }}
                  title="testing"
                  src={`${HOST_API_URL}Api/Invoices/ViewInvoiceFile?id=${params.row.id}&mainFile=true`}
                />
              }
              largeTooltip
              row={params.row}
            />
          )}
        </>
      );
    },
  };

  return (
    <Grid item xs={12}>
      <CustomDataGrid
        gridId="invoicing-invoice-complete"
        key="previous"
        applyQuickFilter
        data={data}
        gridColumns={[...COMPLETED_INVOICES, status, actions]}
        CustomLeftToolbar={CustomToolbar}
        setActiveRows={setActiveRows}
        onCellClick={(row) => {
          const selection = window.getSelection().toString();
          if (selection.length > 0 || row.field === 'actions' || row.field === 'status') return;
          navigate(`/dashboard/invoicing/previous/${row.id}`);
        }}
        isLoading={!data || loading}
        advancedSearchBase={`previousInvoicing`}
      />
    </Grid>
  );
}
